/**
 * Configuration centralisée des icônes du projet
 * 
 * Ce fichier permet de modifier facilement une icône partout en un seul endroit.
 * Pour changer une icône, modifiez simplement l'import et l'export correspondant.
 */

import {
  // Navigation principale
  Home,
  Inbox,
  Users,
  Contact,
  MessageSquare,
  HelpCircle,
  Tag,
  Settings,
  BarChart3,
  
  // Admin navigation
  LayoutDashboard,
  Building2,
  CreditCard,
  FileText,
  Megaphone,
  ScrollText,
  Palette,
  Lightbulb,
  Receipt,
  
  // AI
  Bot,
  Sparkles,
  
  // Actions
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  ArrowLeft,
  ArrowRight,
  Plus,
  Pencil,
  Trash2,
  Save,
  Send,
  Copy,
  Download,
  Upload,
  Link,
  Check,
  X,
  Search,
  ExternalLink,
  FolderPlus,
  UserPlus,
  Edit,
  AlertTriangle,
  Variable,
  
  // Status
  Clock,
  AlertCircle,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Circle,
  Loader2,
  
  // Communication
  Mail,
  MailOpen,
  Bell,
  BellOff,
  MessageSquareText,
  
  // UI Elements
  Eye,
  EyeOff,
  Puzzle,
  Folder,
  FolderOpen,
  User,
  UserCircle,
  Camera,
  Phone,
  MapPin,
  Paperclip,
  Image as ImageIcon,
  GripVertical,
  PanelRightOpen,
  PanelRightClose,
  Bookmark,
  Shield,
  ShieldAlert,
  Ban,
  Crown,
  Globe,
  Calendar,
  Code,
  Plug,
  Hash,
  
  // Theme
  Sun,
  Moon,
  Monitor,
  
  // Sound
  Volume2,
  VolumeX,
  
  // Ratings
  Star,
  ThumbsUp,
  ThumbsDown,
  
  // Charts & Stats
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  CalendarDays,
} from 'lucide-react';

import type { LucideIcon } from 'lucide-react';

// =============================================================================
// NAVIGATION PRINCIPALE
// =============================================================================

/** Icône pour la page d'accueil / dashboard */
export const HomeIcon = Home;

/** Icône pour les tickets (utiliser Inbox pour cohérence visuelle) */
export const TicketsIcon = Inbox;

/** Icône pour les clients */
export const CustomersIcon = Contact;

/** Icône pour les agents / utilisateurs */
export const AgentsIcon = Users;

/** Icône pour les réponses types */
export const ResponsesIcon = MessageSquare;

/** Icône pour la FAQ */
export const FAQIcon = HelpCircle;

/** Icône pour les statistiques */
export const StatisticsIcon = BarChart3;

/** Icône pour l'offre / abonnement */
export const OfferIcon = Tag;

/** Icône pour les réglages */
export const SettingsIcon = Settings;

// =============================================================================
// NAVIGATION ADMIN
// =============================================================================

/** Icône pour le dashboard admin */
export const AdminDashboardIcon = LayoutDashboard;

/** Icône pour les workspaces / espaces */
export const WorkspacesIcon = Building2;

/** Icône pour les abonnements */
export const SubscriptionsIcon = CreditCard;

/** Icône pour les annonces */
export const AnnouncementsIcon = Megaphone;

/** Icône pour le journal d'audit */
export const AuditLogIcon = ScrollText;

/** Icône pour les conseils */
export const TipsIcon = Lightbulb;

/** Icône pour la documentation */
export const DocumentationIcon = FileText;

// =============================================================================
// ONGLETS SETTINGS
// =============================================================================

/** Icône pour l'onglet général */
export const GeneralTabIcon = Building2;

/** Icône pour l'onglet tickets */
export const TicketsTabIcon = Inbox;

/** Icône pour l'onglet notifications */
export const NotificationsTabIcon = Bell;

/** Icône pour l'onglet page publique */
export const PublicPageTabIcon = Eye;

/** Icône pour l'onglet intégrations */
export const IntegrationsTabIcon = Puzzle;

/** Icône pour l'onglet API */
export const ApiTabIcon = Code;

/** Icône pour l'onglet IA */
export const AITabIcon = Sparkles;

/** Icône pour l'agent IA */
export const AIIcon = Bot;

// =============================================================================
// STATUTS DES TICKETS
// =============================================================================

/** Icône pour les nouveaux tickets */
export const NewTicketIcon = AlertCircle;

/** Icône pour les tickets en cours */
export const InProgressTicketIcon = Clock;

/** Icône pour les tickets en attente */
export const WaitingTicketIcon = Clock;

/** Icône pour les tickets fermés */
export const ClosedTicketIcon = XCircle;

/** Icône pour les tickets validés */
export const ValidatedTicketIcon = CheckCircle2;

// =============================================================================
// STATS CARDS
// =============================================================================

/** Icône pour les nouveaux tickets dans les stats */
export const NewTicketsStatsIcon = Mail;

/** Icône pour mes tickets dans les stats */
export const MyTicketsStatsIcon = User;

/** Icône pour les tickets en attente dans les stats */
export const WaitingTicketsStatsIcon = Clock;

/** Icône pour les tickets ouverts dans les stats */
export const OpenTicketsStatsIcon = FolderOpen;

// =============================================================================
// ACTIONS COMMUNES
// =============================================================================

/** Icône pour déconnexion */
export const LogOutIcon = LogOut;

/** Icône pour retour */
export const BackIcon = ArrowLeft;

/** Icône pour navigation précédente */
export const ChevronLeftIcon = ChevronLeft;

/** Icône pour navigation suivante */
export const ChevronRightIcon = ChevronRight;

/** Icône pour expand/collapse haut */
export const ChevronUpIcon = ChevronUp;

/** Icône pour expand/collapse bas */
export const ChevronDownIcon = ChevronDown;

/** Icône pour ajouter */
export const AddIcon = Plus;

/** Icône pour éditer */
export const EditIcon = Pencil;

/** Icône pour supprimer */
export const DeleteIcon = Trash2;

/** Icône pour sauvegarder */
export const SaveIcon = Save;

/** Icône pour envoyer */
export const SendIcon = Send;

/** Icône pour copier */
export const CopyIcon = Copy;

/** Icône pour télécharger */
export const DownloadIcon = Download;

/** Icône pour upload */
export const UploadIcon = Upload;

/** Icône pour lien */
export const LinkIcon = Link;

/** Icône pour confirmer */
export const CheckIcon = Check;

/** Icône pour fermer */
export const CloseIcon = X;

/** Icône pour recherche */
export const SearchIcon = Search;

/** Icône pour lien externe */
export const ExternalLinkIcon = ExternalLink;

/** Icône pour ajouter un dossier */
export const FolderPlusIcon = FolderPlus;

/** Icône pour inviter un utilisateur */
export const UserPlusIcon = UserPlus;

/** Icône pour tag */
export const TagIcon = Tag;

/** Icône pour bouclier (admin) */
export const ShieldIcon = Shield;
export const SpamIcon = ShieldAlert;
export const BanIcon = Ban;

/** Icône pour couronne (owner) */
export const CrownIcon = Crown;

// =============================================================================
// UI ELEMENTS
// =============================================================================

/** Icône pour chargement */
export const LoaderIcon = Loader2;

/** Icône pour indicateur en ligne */
export const OnlineIndicatorIcon = Circle;

/** Icône pour dossier */
export const FolderIcon = Folder;

/** Icône pour dossier ouvert */
export const FolderOpenIcon = FolderOpen;

/** Icône pour pièce jointe */
export const AttachmentIcon = Paperclip;

/** Icône pour image */
export const ImageIcon_ = ImageIcon;

/** Icône pour drag & drop */
export const DragHandleIcon = GripVertical;

/** Icône pour panneau latéral ouvert */
export const PanelOpenIcon = PanelRightOpen;

/** Icône pour panneau latéral fermé */
export const PanelCloseIcon = PanelRightClose;

/** Icône pour favoris */
export const BookmarkIcon = Bookmark;

/** Icône pour utilisateur */
export const UserIcon = User;

/** Icône pour avatar utilisateur */
export const UserAvatarIcon = UserCircle;

/** Icône pour photo/caméra */
export const CameraIcon = Camera;

/** Icône pour téléphone */
export const PhoneIcon = Phone;

/** Icône pour adresse / localisation */
export const AddressIcon = MapPin;

/** Icône pour globe / internet */
export const GlobeIcon = Globe;

/** Icône pour navigateur */
export const BrowserIcon = Monitor;

/** Icône pour calendrier simple */
export const CalendarSimpleIcon = Calendar;

/** Icône pour variable */
export const VariableIcon = Variable;

/** Icône pour message texte */
export const MessageTextIcon = MessageSquareText;

/** Icône pour alerte / avertissement */
export const WarningIcon = AlertTriangle;

/** Icône pour API */
export const ApiIcon = Code;

/** Icône pour connecteur / plugin */
export const PlugIcon = Plug;

/** Icône pour numéro / identifiant */
export const HashIcon = Hash;

// =============================================================================
// NOTIFICATIONS & COMMUNICATION
// =============================================================================

/** Icône pour email */
export const EmailIcon = Mail;

/** Icône pour email ouvert */
export const MailOpenIcon = MailOpen;

/** Icône pour alerte cercle */
export const AlertCircleIcon = AlertCircle;

/** Icône pour alerte triangle */
export const AlertTriangleIcon = AlertTriangle;

/** Icône pour notification active */
export const NotificationOnIcon = Bell;

/** Icône pour notification désactivée */
export const NotificationOffIcon = BellOff;

// =============================================================================
// THÈME
// =============================================================================

/** Icône pour thème clair */
export const LightModeIcon = Sun;

/** Icône pour thème sombre */
export const DarkModeIcon = Moon;

/** Icône pour thème système */
export const SystemModeIcon = Monitor;

// =============================================================================
// MODE NE PAS DÉRANGER
// =============================================================================

/** Icône pour le mode Ne pas déranger */
export const MoonIcon = Moon;

/** Icône pour la durée / horloge */
export const ClockIcon = Clock;

// =============================================================================
// AUDIO
// =============================================================================

/** Icône pour son activé */
export const SoundOnIcon = Volume2;

/** Icône pour son désactivé */
export const SoundOffIcon = VolumeX;

// =============================================================================
// RATINGS & ÉVALUATIONS
// =============================================================================

/** Icône pour étoile */
export const StarIcon = Star;

/** Icône pour avis positif */
export const ThumbsUpIcon = ThumbsUp;

/** Icône pour avis négatif */
export const ThumbsDownIcon = ThumbsDown;

// =============================================================================
// CHARTS & STATISTIQUES
// =============================================================================

/** Icône pour tendance */
export const TrendingUpIcon = TrendingUp;

/** Icône pour flèche haut droite */
export const ArrowUpRightIcon = ArrowUpRight;

/** Icône pour flèche bas droite */
export const ArrowDownRightIcon = ArrowDownRight;

/** Icône pour calendrier */
export const CalendarIcon = CalendarDays;

/** Icône pour visibilité */
export const VisibilityIcon = Eye;

/** Icône pour visibilité cachée */
export const VisibilityOffIcon = EyeOff;

// =============================================================================
// TYPES
// =============================================================================

export type { LucideIcon };

// =============================================================================
// CONFIGURATION DE NAVIGATION
// =============================================================================

export interface NavItem {
  title: string;
  url: string;
  icon: LucideIcon;
  hasBadge?: boolean;
  multiline?: boolean;
  end?: boolean;
}

/** Navigation principale de l'application */
export const mainNavItems: NavItem[] = [
  { title: 'Accueil', url: '/', icon: HomeIcon },
  { title: 'Tickets', url: '/tickets', icon: TicketsIcon, hasBadge: true },
  { title: 'Agents', url: '/agents', icon: AgentsIcon },
  { title: 'Clients', url: '/customers', icon: CustomersIcon },
  { title: 'Réponses Types', url: '/responses', icon: ResponsesIcon, multiline: true },
  { title: 'FAQ', url: '/faq', icon: FAQIcon },
  { title: 'Statistiques', url: '/statistics', icon: StatisticsIcon },
  { title: 'Réglages', url: '/settings', icon: SettingsIcon },
];

/** Icône pour les emails */
export const EmailPreviewsIcon = Mail;

/** Icône pour le design system */
export const DesignSystemIcon = Palette;

/** Icône pour limites de plans */
export const PlanLimitsIcon = BarChart3;

/** Icône pour les paiements */
export const PaymentsIcon = Receipt;

/** Interface pour les groupes de navigation admin */
export interface AdminNavGroup {
  label: string;
  items: NavItem[];
}

/** Navigation admin organisée par groupes */
export const adminNavGroups: AdminNavGroup[] = [
  {
    label: 'Gestion',
    items: [
      { title: 'Dashboard', url: '/admin', icon: AdminDashboardIcon, end: true },
      { title: 'Espaces', url: '/admin/workspaces', icon: WorkspacesIcon },
      { title: 'Utilisateurs', url: '/admin/users', icon: AgentsIcon },
    ],
  },
  {
    label: 'Facturation',
    items: [
      { title: 'Abonnements', url: '/admin/subscriptions', icon: SubscriptionsIcon },
      { title: 'Paiements', url: '/admin/payments', icon: Receipt },
      { title: 'Limites plans', url: '/admin/plan-limits', icon: BarChart3 },
    ],
  },
  {
    label: 'IA',
    items: [
      { title: 'Config Points IA', url: '/admin/ai-points', icon: Bot },
      { title: 'Usage Points IA', url: '/admin/ai-usage', icon: TrendingUp },
    ],
  },
  {
    label: 'Emails',
    items: [
      { title: 'Stats Emails', url: '/admin/email-stats', icon: Mail },
      { title: 'Prévisualisations', url: '/admin/emails', icon: Mail },
      { title: 'Doc Emails', url: '/admin/email-docs', icon: FileText },
    ],
  },
  {
    label: 'Communication',
    items: [
      { title: 'Annonces', url: '/admin/announcements', icon: AnnouncementsIcon },
      { title: 'Conseils', url: '/admin/tips', icon: Lightbulb },
    ],
  },
  {
    label: 'Système',
    items: [
      { title: 'Journal d\'audit', url: '/admin/audit-logs', icon: AuditLogIcon },
      { title: 'Design System', url: '/admin/design-system', icon: Palette },
      { title: 'Documentation', url: '/admin/documentation', icon: DocumentationIcon },
      { title: 'Configuration', url: '/admin/settings', icon: SettingsIcon },
    ],
  },
];

/** Navigation admin (liste plate pour compatibilité) */
export const adminNavItems: NavItem[] = adminNavGroups.flatMap(group => group.items);
